import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/api";
import { toast } from "sonner";
import SilhouettePicker from "@/components/SilhouettePicker";
import { LogOut, Shield } from "lucide-react";

const GOALS = ["Gain muscle", "Lose weight", "Build muscle, lose fat", "Stay fit"];
const ACTIVITIES = ["Light", "Moderate", "Heavy", "Athlete"];

export default function Profile() {
  const { user, setUser, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [weight, setWeight] = useState(user?.weightKg ?? "");
  const [bfMode, setBfMode] = useState("silhouette"); // silhouette|tape
  const [tape, setTape] = useState({ neck: "", waist: "", hip: "" });

  const save = async (patch, evLabel) => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", patch);
      setUser(data);
      toast.success(evLabel || "Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const logWeight = async () => {
    const w = Number(weight);
    if (!Number.isFinite(w) || w <= 0 || w > 300) { toast.error("Weight 0–300 kg"); return; }
    await api.post("/log/weight", { weightKg: w });
    await refreshUser();
    toast.success("Weight logged");
  };

  const computeTape = async () => {
    try {
      const { data } = await api.post("/measure/navy", {
        gender: user.gender, heightCm: user.heightCm, neckCm: Number(tape.neck), waistCm: Number(tape.waist), hipCm: user.gender === "Female" ? Number(tape.hip) : null,
      });
      await api.post("/log/bodyfat", { bodyFatPct: data.bodyFatPct, source: "tape" });
      await refreshUser();
      toast.success(`Body fat: ${data.bodyFatPct}%`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid measurements"); }
  };

  const logBfSilhouette = async (pct) => {
    await api.post("/log/bodyfat", { bodyFatPct: pct, source: "silhouette" });
    await refreshUser();
    toast.success("Body fat updated");
  };

  return (
    <div className="min-h-screen bg-app max-w-md mx-auto px-5 pt-10 pb-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Profile</div>
          <h1 className="font-display text-3xl uppercase mt-2" data-testid="profile-name">{user?.name}</h1>
          <div className="text-t2 text-sm mt-1">{user?.email}</div>
        </div>
        <button onClick={async () => { await logout(); nav("/"); }} className="stepper" data-testid="profile-logout"><LogOut size={16} /></button>
      </div>

      {user?.role === "admin" && (
        <button onClick={() => nav("/admin")} className="mt-6 w-full card p-4 flex items-center justify-between hover:border-hair transition-colors" data-testid="profile-admin-link">
          <div className="flex items-center gap-3">
            <span className="stepper" style={{ borderColor: "var(--lime)", color: "var(--lime)" }}><Shield size={16} /></span>
            <div className="text-left">
              <div className="font-display uppercase">Admin</div>
              <div className="text-t3 text-xs mt-0.5">Users, sessions, growth</div>
            </div>
          </div>
          <span className="text-t3 text-sm">→</span>
        </button>
      )}

      <Section title="Goal">
        <div className="grid grid-cols-2 gap-2">
          {GOALS.map((g) => (
            <button key={g} onClick={() => save({ goal: g })} className={`chip ${user?.goal === g ? "selected" : ""}`} data-testid={`prof-goal-${g}`}>{g}</button>
          ))}
        </div>
      </Section>

      <Section title="Days per week">
        <div className="grid grid-cols-4 gap-2">
          {[2, 3, 4, 5].map((d) => (
            <button key={d} onClick={() => save({ daysPerWeek: d }, "Split rebuilt")} className={`chip ${user?.daysPerWeek === d ? "selected" : ""}`} data-testid={`prof-days-${d}`}>{d}</button>
          ))}
        </div>
      </Section>

      <Section title="Activity level">
        <div className="grid grid-cols-2 gap-2">
          {ACTIVITIES.map((a) => (
            <button key={a} onClick={() => save({ activity: a })} className={`chip ${user?.activity === a ? "selected" : ""}`} data-testid={`prof-activity-${a}`}>{a}</button>
          ))}
        </div>
      </Section>

      <Section title="Bodyweight">
        <div className="flex gap-2">
          <input type="number" step="0.1" className="input" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="kg" data-testid="prof-weight-input" />
          <button className="btn btn-outline" onClick={logWeight} data-testid="prof-weight-save">Log</button>
        </div>
      </Section>

      <Section title="Body fat">
        <div className="flex gap-2 mb-3">
          <button className={`chip ${bfMode === "silhouette" ? "selected" : ""}`} onClick={() => setBfMode("silhouette")} data-testid="bf-mode-silhouette">Silhouette</button>
          <button className={`chip ${bfMode === "tape" ? "selected" : ""}`} onClick={() => setBfMode("tape")} data-testid="bf-mode-tape">Navy tape</button>
        </div>
        {bfMode === "silhouette" ? (
          <SilhouettePicker gender={user?.gender || "Male"} value={user?.bodyFatPct} onChange={logBfSilhouette} />
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input className="input" placeholder="Neck (cm)" type="number" step="0.1" value={tape.neck} onChange={(e) => setTape({ ...tape, neck: e.target.value })} data-testid="tape-neck" />
              <input className="input" placeholder="Waist (cm)" type="number" step="0.1" value={tape.waist} onChange={(e) => setTape({ ...tape, waist: e.target.value })} data-testid="tape-waist" />
              {user?.gender === "Female" && (
                <input className="input col-span-2" placeholder="Hip (cm)" type="number" step="0.1" value={tape.hip} onChange={(e) => setTape({ ...tape, hip: e.target.value })} data-testid="tape-hip" />
              )}
            </div>
            <button className="btn btn-outline w-full" onClick={computeTape} data-testid="tape-compute">Compute body fat</button>
            <div className="text-xs text-t3">Tape is authoritative when both methods are used.</div>
          </div>
        )}
      </Section>

      <Section title="Units">
        <div className="flex gap-2">
          {[{v:"metric",l:"Metric (kg, cm)"},{v:"imperial",l:"Imperial (lb, in)"}].map((o) => (
            <button key={o.v} onClick={() => save({ unitsPref: o.v })} className={`chip flex-1 ${user?.unitsPref === o.v ? "selected" : ""}`} data-testid={`units-${o.v}`}>{o.l}</button>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6 card p-4">
      <div className="overline">{title}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}
